import React, { useCallback, useEffect, useState } from "react";
import {
  FaToggleOn,
  FaToggleOff,
  FaCheckCircle,
  FaClock,
  FaHourglassEnd,
  FaSyncAlt,
  FaExclamationTriangle,
  FaGift,
} from "react-icons/fa";
import { MdSubscriptions } from "react-icons/md";
import AdminService from "../../services/adminService";
import SubscriptionCharts from "./SubscriptionCharts";

const TONES = {
  blue: "bg-blue-50 text-blue-600",
  green: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  gray: "bg-gray-100 text-gray-500",
  orange: "bg-orange-50 text-orange-600",
};

const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const share = (part, whole) => (whole ? `${Math.round((part / whole) * 100)}%` : "0%");

const KpiCard = ({ label, value, hint, icon, tone }) => (
  <div className="bg-white rounded-xl border border-gray-200/70 shadow-sm p-4 min-w-0">
    <div className="flex items-center gap-2">
      <span className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-xs ${TONES[tone]}`}>
        {icon}
      </span>
      <p className="text-xs font-medium text-gray-500 truncate">{label}</p>
    </div>
    <p className="mt-2 text-2xl font-semibold text-gray-900 tabular-nums">{fmt(value)}</p>
    <p className="mt-0.5 text-xs text-gray-500 truncate" title={hint}>{hint}</p>
  </div>
);

const SubscriptionStat = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    const data = await AdminService.fetchSubscriptionStats();
    if (data) {
      setStats(data);
      setUpdatedAt(new Date());
      setFailed(false);
    } else {
      setFailed(true);
    }
    setRefreshing(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const header = (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h3 className="text-base font-semibold text-gray-800">Subscriptions</h3>
        <p className="text-xs text-gray-500">
          Subscribers only. Admins, app users, deleted accounts and test purchases are excluded.
        </p>
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-500">
        {failed && stats && <span className="text-red-600">Refresh failed</span>}
        {updatedAt && (
          <span>
            Updated {updatedAt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
          </span>
        )}
        <button
          type="button"
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          <FaSyncAlt className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <section className="mt-8 space-y-4">
        {header}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-[98px] bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
        <div className="h-80 bg-gray-100 rounded-xl animate-pulse" />
      </section>
    );
  }

  if (!stats) {
    return (
      <section className="mt-8 space-y-4">
        {header}
        <div className="bg-white rounded-xl border border-gray-200/70 shadow-sm p-4 text-sm text-red-600">
          Subscription stats could not be loaded. Try Refresh.
        </div>
      </section>
    );
  }

  const byStatus = stats.byStatus || {};
  const trial = stats.onTrial || 0;
  const expired = byStatus.expired || 0;
  const paid = stats.paidActive || 0;
  // Free promotional grants: access, but not paying — never part of `paid`.
  const promotional = stats.promotionalActive || 0;
  const billing = (byStatus.billing_issue || 0) + (byStatus.grace_period || 0);

  const kpis = [
    { label: "Total", value: stats.total, hint: "Trial, paid or promotional, ever", icon: <MdSubscriptions />, tone: "blue" },
    { label: "Paid active", value: paid, hint: `${share(paid, stats.total)} of total`, icon: <FaCheckCircle />, tone: "green" },
    { label: "On trial", value: trial, hint: `${share(trial, stats.total)} of total`, icon: <FaClock />, tone: "amber" },
    { label: "Free (promotional)", value: promotional, hint: "Not paying · does not renew", icon: <FaGift />, tone: "blue" },
    { label: "Expired", value: expired, hint: `${share(expired, stats.total)} of total`, icon: <FaHourglassEnd />, tone: "gray" },
    {
      label: "Auto-renew on",
      value: stats.autoRenewOn,
      hint: paid ? `${share(stats.autoRenewOn, paid)} of paid` : "No paid users yet",
      icon: <FaToggleOn />,
      tone: "green",
    },
    { label: "Auto-renew off", value: stats.autoRenewOff, hint: "Lapses at period end", icon: <FaToggleOff />, tone: "orange" },
  ];

  const alerts = [
    { label: "billing issue", value: billing },
    { label: "refunded", value: stats.refunded || 0 },
  ].filter((a) => a.value > 0);

  return (
    <section className="mt-8 space-y-4">
      {header}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpis.map((k) => (
          <KpiCard key={k.label} {...k} />
        ))}
      </div>

      {alerts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {alerts.map((a) => (
            <span
              key={a.label}
              className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-medium text-red-700"
            >
              <FaExclamationTriangle />
              {fmt(a.value)} {a.label}
            </span>
          ))}
        </div>
      )}

      <SubscriptionCharts stats={stats} />
    </section>
  );
};

export default SubscriptionStat;
